import { useState } from "react";
import {
  FEATURE_REQUEST_SOURCE_LABELS,
  FeatureRequestSource,
  ProductFeature,
} from "../../../api/featureRequests";
import {
  useCreateFeatureMutation,
  useCreateRequestMutation,
  useProductFeaturesQuery,
} from "../hooks/useFeatureRequestsQueries";
import FeatureAutocomplete, { FeatureChoice } from "./FeatureAutocomplete";
import {
  errorMessage,
  fromDateInputValue,
  isValidEmail,
  toDateInputValue,
} from "../utils";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSTextField from "../../system-design/DSTextField";
import DSEnumSelect from "../../system-design/DSEnumSelect";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSAlert from "../../system-design/DSAlert";
import DSBox from "../../system-design/DSBox";

const SOURCE_OPTIONS = Object.values(FeatureRequestSource).map((source) => ({
  value: source,
  label: FEATURE_REQUEST_SOURCE_LABELS[source],
}));

const AddRequestModal = (props: {
  open: boolean;
  onClose: () => void;
  defaultFeature?: ProductFeature;
}) => {
  const { data: features = [] } = useProductFeaturesQuery();
  const createFeature = useCreateFeatureMutation();
  const createRequest = useCreateRequestMutation();

  const [feature, setFeature] = useState<FeatureChoice | null>(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [date, setDate] = useState(toDateInputValue(Date.now()));
  const [source, setSource] = useState<FeatureRequestSource>(
    FeatureRequestSource.Email,
  );
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [lastAdded, setLastAdded] = useState<string | null>(null);

  const resetCustomer = () => {
    setEmail("");
    setName("");
    setNotes("");
    setError(null);
  };

  // Runs each time the dialog opens (not on re-render), so refetched feature
  // objects after a save don't wipe the form.
  const resetForm = () => {
    setFeature(
      props.defaultFeature
        ? { kind: "existing", feature: props.defaultFeature }
        : null,
    );
    setDate(toDateInputValue(Date.now()));
    setSource(FeatureRequestSource.Email);
    setLastAdded(null);
    resetCustomer();
  };

  const isSaving = createFeature.isPending || createRequest.isPending;
  const emailOk = isValidEmail(email);
  const canSave = !!feature && emailOk && !!date && !isSaving;

  // "Add another" keeps the feature, date and source so a batch of requests
  // for the same feature can be logged quickly.
  const save = async (keepOpen: boolean) => {
    if (!feature) return;
    setError(null);
    try {
      let featureId: string;
      let featureTitle: string;
      if (feature.kind === "new") {
        const created = await createFeature.mutateAsync({
          title: feature.title,
        });
        featureId = created.id;
        featureTitle = created.title;
        // Later submissions in this session must attach to the feature just
        // created rather than creating it again.
        setFeature({ kind: "existing", feature: created });
      } else {
        featureId = feature.feature.id;
        featureTitle = feature.feature.title;
      }
      await createRequest.mutateAsync({
        featureId,
        customerEmail: email.trim(),
        customerName: name.trim() || null,
        requestedAt: fromDateInputValue(date),
        source,
        notes: notes.trim() || null,
      });
      if (keepOpen) {
        setLastAdded(`Added ${email.trim()} to "${featureTitle}"`);
        resetCustomer();
      } else {
        props.onClose();
      }
    } catch (e) {
      setError(errorMessage(e, "Failed to save the request."));
    }
  };

  return (
    <DSDialog
      open={props.open}
      onClose={props.onClose}
      fullWidth
      maxWidth="sm"
      TransitionProps={{ onEnter: resetForm }}
    >
      <DSDialogTitle>Add feature request</DSDialogTitle>
      <DSDialogContent>
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <FeatureAutocomplete
            features={features}
            value={feature}
            onChange={setFeature}
            allowCreate
            autoFocus={!props.defaultFeature}
          />
          <DSTextField
            label="Customer email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={email.length > 0 && !emailOk}
            helperText={
              email.length > 0 && !emailOk ? "Not a valid email" : undefined
            }
            autoFocus={!!props.defaultFeature}
          />
          <DSTextField
            label="Customer name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <DSBox sx={{ display: "flex", gap: 2 }}>
            <DSTextField
              label="Date requested"
              type="date"
              required
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <DSEnumSelect<FeatureRequestSource>
              value={source}
              options={SOURCE_OPTIONS}
              onChange={setSource}
              sx={{ flex: 1 }}
            />
          </DSBox>
          <DSTextField
            label="Notes (optional)"
            placeholder="What exactly did the customer ask for?"
            multiline
            minRows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          {lastAdded && <DSAlert severity="success">{lastAdded}</DSAlert>}
          {error && <DSAlert severity="error">{error}</DSAlert>}
        </DSBox>
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={props.onClose}>Close</DSSecondaryButton>
        <DSSecondaryButton disabled={!canSave} onClick={() => save(true)}>
          Save &amp; add another
        </DSSecondaryButton>
        <DSPrimaryButton disabled={!canSave} onClick={() => save(false)}>
          Save
        </DSPrimaryButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default AddRequestModal;
